import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../../hooks/redux';
import { FormInputText } from '../../../../inputs/FormInputText';
import { ButtonClassNames, ModalVariants, InputRegexPattern } from '../../../../../models/Common';
import { CustomMUIButton } from '../../../buttons/CustomMUIButton';
import { ApiError } from '../../../../../models/ApiError';
import { modalSlice } from '../../../../../store/reducers/ModalSlice';
import { internetApi } from '../../../../../services/apiServices/user/internet';
import { useCustomSnackbar } from '../../../../../hooks/useCustomEnqueueSnackbar';
import { ConnectionProtocolTypes } from '../../../../../models/Accounts';
import { getPpoeSettingsLink, getErrorMessage } from '../../../../../utils';
import { FormInputPassword } from '../../../../inputs/FormInputPassword';

interface EditInternetLoginProps {
  handleClose: () => void;
  inputLogin: string;
  accountId: string | number;
  connectionProtocolId: number;
  selectedLanguage: string;
}

interface IEditInternetLoginForm {
  login: string;
  currentPassword: string;
}

export const EditInternetLogin: FC<EditInternetLoginProps> = ({
  handleClose,
  inputLogin,
  accountId,
  connectionProtocolId,
  selectedLanguage,
}) => {
  const { t } = useTranslation();
  const methods = useForm<IEditInternetLoginForm>({ defaultValues: { login: inputLogin } });
  const { handleSubmit, setError } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [editInternetDevice, { isLoading }] = internetApi.useUpdateLoginOrPasswordMutation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;

  const handleEditLogin = async ({ login, currentPassword }: IEditInternetLoginForm) => {
    try {
      await editInternetDevice({ login, accountId, currentPassword })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.internetDeviceLoginChanged'), 'success');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof IEditInternetLoginForm, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof IEditInternetLoginForm>;
        apiErrorsKeys.forEach((element) => {
          setError(element, {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.loginChange')}</article>
        {connectionProtocolId === ConnectionProtocolTypes.PPPoE && (
          <div className="modals__attention">
            {t('serviceManagement.afterChangingDataInternetConnectionWillBeInterrupted')}{' '}
            <a href={getPpoeSettingsLink(selectedLanguage)} rel="noreferrer" target="_blank">
              {t('serviceManagement.pppoeConnectionSettings')}
            </a>
          </div>
        )}
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleEditLogin)}>
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('auth.enterPassLk')}
            isRequired
            minLength={6}
            maxLength={16}
            fieldHasName
          />
          <FormInputText
            name="login"
            className="primary"
            label={t('common.newLogin')}
            helperText={t('input.numberOfCharactersInLowerCaseNumbersSigns', { from: 3, to: 16 })}
            isRequired
            minLength={3}
            maxLength={16}
            pattern={InputRegexPattern.InternetLogin}
          />
          <div className="modals__btns-wrapper">
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('buttons.save')}
                type="submit"
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
                isLoading={isLoading}
              />
            </div>
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('auth.forgotMyPassword')}
                handleClick={handleForgotPassword}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
                type="button"
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
